import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { fonts, colors, spacing, fontWeights, borderRadius } from '../theme';
import type { CashflowRow } from '../core/calculations';
import { ColumnInfoModal, ColumnKey } from './ColumnInfoModal';

export interface M07EstimationTableProps {
  cashflow: CashflowRow[];
  basePremium: number;
  selectedScenario: 'high' | 'low';
  setSelectedScenario: (s: 'high' | 'low') => void;
  showFullTable?: boolean;
  setShowFullTable?: (b: boolean) => void;
}

const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export const M07EstimationTable: React.FC<M07EstimationTableProps> = ({
  cashflow,
  basePremium,
  selectedScenario,
  setSelectedScenario,
  showFullTable: propShowFullTable,
  setShowFullTable: propSetShowFullTable,
}) => {
  const [internalShowFullTable, setInternalShowFullTable] = useState(false);
  const showFullTable = propShowFullTable ?? internalShowFullTable;
  const setShowFullTable = propSetShowFullTable ?? setInternalShowFullTable;
  const [infoColumnKey, setInfoColumnKey] = useState<string | null>(null);

  const renderTh = (label: string, colKey: string) => (
    <TouchableOpacity
      style={[styles.headerCellBtn, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', paddingHorizontal: 2 }, { cursor: 'pointer' } as any]}
      onPress={() => setInfoColumnKey(colKey)}
      activeOpacity={0.6}
    >
      <Text style={[styles.detailTableHeaderCellText, { flexShrink: 1, textAlign: 'right' }]}>{label}</Text>
      <Text style={[styles.headerInfoBadge, { paddingLeft: 3 }]}>ⓘ</Text>
    </TouchableOpacity>
  );

  return (
    <View style={styles.subTabContent}>
      <ColumnInfoModal
        visible={!!infoColumnKey}
        columnKey={infoColumnKey}
        onClose={() => setInfoColumnKey(null)}
      />

      {/* Scenario Toggle */}
      <View style={styles.tableScenarioHeader}>
        <Text style={styles.tableScenarioTitle}>Mô phỏng dòng tiền kịch bản</Text>
        <View style={styles.scenarioToggleContainer}>
          <TouchableOpacity
            style={[styles.scenarioTogglePill, selectedScenario === 'high' && styles.scenarioTogglePillActive]}
            onPress={() => setSelectedScenario('high')}
          >
            <Text style={[styles.scenarioTogglePillText, selectedScenario === 'high' && styles.scenarioTogglePillActiveText]}>
              Lãi suất 4.0%
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.scenarioTogglePill, selectedScenario === 'low' && styles.scenarioTogglePillActive]}
            onPress={() => setSelectedScenario('low')}
          >
            <Text style={[styles.scenarioTogglePillText, selectedScenario === 'low' && styles.scenarioTogglePillActiveText]}>
              Lãi suất 0.5%
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.responsiveTableWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          contentContainerStyle={styles.tableScrollContent}
          style={styles.tableScroll}
        >
          <View style={styles.detailTable}>
            <View style={[styles.detailTableHeader, { backgroundColor: '#00A651' }]}>
              {renderTh('NĂM/TUỔI', ColumnKey.POLICY_YEAR)}
              {renderTh('PHÍ BAN ĐẦU', ColumnKey.INITIAL_CHARGE)}
              {renderTh('PHÍ CƠ BẢN', ColumnKey.BASE_PREMIUM)}
              {renderTh('SP BỔ TRỢ', ColumnKey.RIDER_PREMIUM)}
              {renderTh('ĐẦU TƯ THÊM', ColumnKey.TOPUP_PAID)}
              {renderTh('RÚT GTTK', ColumnKey.WITHDRAWAL_TOTAL)}
              {renderTh('TIỀN ĐI ĐT', ColumnKey.ALLOCATED_PREMIUM)}
              {renderTh('PHÍ KHẤU TRỪ', ColumnKey.TOTAL_DEDUCTION)}
              {renderTh(`GTC (${selectedScenario === 'high' ? '4.0%' : '0.5%'})`, ColumnKey.ACCOUNT_VALUE)}
              {renderTh('GTHL KHI KT HĐ', ColumnKey.SURRENDER_VALUE)}
            </View>
            {cashflow.map((row) => {
              if (!showFullTable && row.year > 4 && row.year !== 10 && row.year !== 20) return null;
              const isSpecialRow = row.year === 10 || row.year === 20;
              return (
                <View key={row.year} style={[styles.detailTableRow, isSpecialRow && { backgroundColor: '#f0fdf4' }]}>
                  <Text style={[styles.detailTableCell, { color: '#0066cc' }]}>{row.year} / {row.age}</Text>
                  <Text style={styles.detailTableCell}>{formatNumber(row.initialCharge)}</Text>
                  <Text style={styles.detailTableCell}>{formatNumber(basePremium)}</Text>
                  <Text style={styles.detailTableCell}>{formatNumber(row.riderPremium)}</Text>
                  <Text style={[styles.detailTableCell, { color: '#0ea5e9' }]}>{formatNumber(row.topUpPaid)}</Text>
                  <Text style={styles.detailTableCell}>{formatNumber(row.withdrawalTotal || 0)}</Text>
                  <Text style={styles.detailTableCell}>{formatNumber(row.allocatedBasePremium)}</Text>
                  <Text style={[styles.detailTableCell, { color: '#e11d48' }]}>{formatNumber(row.totalDeduction)}</Text>
                  <Text style={[styles.detailTableCell, { fontWeight: isSpecialRow ? 'bold' : 'normal' }]}>{formatNumber(row.accountValue)}</Text>
                  <Text style={[styles.detailTableCell, { color: '#00A651', fontWeight: 'bold' }]}>{formatNumber(row.surrenderValue)}</Text>
                </View>
              );
            })}
          </View>
        </ScrollView>
      </View>
      <TouchableOpacity style={styles.tableToggle} onPress={() => setShowFullTable(!showFullTable)}>
        <Text style={styles.tableToggleText}>{showFullTable ? 'Thu gọn bảng' : 'Xem bảng đầy đủ →'}</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  subTabContent: {
    gap: spacing[2],
  },
  responsiveTableWrapper: {
    width: '100%',
  },
  tableScroll: {
    width: '100%',
  },
  tableScrollContent: {
    minWidth: '100%',
  },
  tableScenarioHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing[2],
  },
  tableScenarioTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  scenarioToggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#EDF1F5',
    borderRadius: borderRadius.full,
    padding: 3,
  },
  scenarioTogglePill: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: borderRadius.full,
  },
  scenarioTogglePillActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 1,
    elevation: 1,
  },
  scenarioTogglePillText: {
    fontSize: 10.5,
    fontWeight: fontWeights.semibold,
    color: '#64748b',
  },
  scenarioTogglePillActiveText: {
    color: colors.primary,
    fontWeight: fontWeights.bold,
  },
  detailTable: {
    width: '100%',
    minWidth: 720,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
  },
  // Visible only when content needs horizontal scroll (e.g. mobile)
  detailTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#00A651',
  },
  detailTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    backgroundColor: '#ffffff',
  },
  detailTableCell: {
    flex: 1,
    minWidth: 80,
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[2],
    fontSize: 11,
    color: '#334155',
    textAlign: 'right',
    fontFamily: fonts.mono,
    fontVariant: ['tabular-nums'],
  },
  headerCellBtn: {
    flex: 1,
    minWidth: 80,
    paddingVertical: spacing[2],
    paddingHorizontal: spacing[2],
  },
  detailTableHeaderCell: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    textAlign: 'right',
    fontFamily: fonts.body,
  },
  detailTableHeaderCellText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 11,
    fontFamily: fonts.body,
  },
  headerInfoBadge: {
    fontSize: 11,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 2,
    opacity: 0.9,
  },
  tableToggle: {
    alignItems: 'center',
    paddingVertical: spacing[3],
  },
  tableToggleText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.secondary,
  },
});
